import { useMemo, memo } from "react";
import { Chart } from "primereact/chart";

import { getComputedColor } from "sj-ui-toolkit/src/atlas/utils/colorUtils";

import { PROFICIENCY_THRESHOLDS, formatChartDate } from "./assessmentHelpers";
import S from "./Assessments.module.scss";

/**
 * Chart.js plugin: draws a horizontal tick on each bar at the proficiency
 * threshold that score reached (60 for Intermediate, 80 for Advanced).
 */
const proficiencyTickPlugin = {
  id: "proficiencyTick",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    const yScale = chart.scales.y;
    const tickColor = getComputedColor("--text-muted");

    ctx.save();
    ctx.strokeStyle = tickColor;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([3, 3]);

    meta.data.forEach(bar => {
      const value = bar.$context.parsed.y;
      let threshold = null;
      if (value >= PROFICIENCY_THRESHOLDS.PROFICIENCIES)
        threshold = PROFICIENCY_THRESHOLDS.PROFICIENCIES;
      else threshold = PROFICIENCY_THRESHOLDS.GROWTH_OPPORTUNITIES;

      if (threshold != null) {
        const y = yScale.getPixelForValue(threshold);
        const halfWidth = bar.width / 2 + 3;
        ctx.beginPath();
        ctx.moveTo(bar.x - halfWidth, y);
        ctx.lineTo(bar.x + halfWidth, y);
        ctx.stroke();
      }
    });

    ctx.restore();
  },
};

/** Memoized bar chart of historical scores — prevents flicker during parent re-renders */
const ScoreHistoryChart = memo(({ scores }) => {
  const chartData = useMemo(() => {
    const recent = scores.slice(-3);
    const labels = recent.map(s => formatChartDate(s.completedAt));
    const values = recent.map(s => Math.round(s.scorePercent ?? 0));
    const barColor = getComputedColor("--assessment-score-fill");

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: barColor,
          borderRadius: 3,
          barThickness: 20,
          maxBarThickness: 20,
        },
      ],
    };
  }, [scores]);

  const chartOptions = useMemo(() => {
    const axisColor = getComputedColor("--text-muted");
    const tickColor = getComputedColor("--text-muted-aaa");

    return {
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          displayColors: false,
          callbacks: {
            title: () => "",
            label: context => `${context.parsed.y}%`,
          },
        },
      },
      scales: {
        x: {
          display: true,
          grid: { display: false },
          border: { display: true, color: axisColor, dash: [4, 4] },
          ticks: {
            font: { size: 10 },
            color: tickColor,
          },
        },
        y: {
          display: false,
          beginAtZero: true,
          max: 100,
        },
      },
    };
  }, []);

  // Memoize plugins array to prevent Chart re-initialization
  const plugins = useMemo(() => [proficiencyTickPlugin], []);

  if (!scores.length) return null;

  const recent = scores.slice(-3);
  const summaryRows = recent.map(s => ({
    label: formatChartDate(s.completedAt),
    value: Math.round(s.scorePercent ?? 0),
  }));
  const ariaLabel = `Score history bar chart. ${summaryRows
    .map(r => `${r.label}: ${r.value} percent`)
    .join("; ")}.`;

  return (
    <div className={S.chartWrapper}>
      <Chart
        type="bar"
        data={chartData}
        options={chartOptions}
        plugins={plugins}
        style={{ height: "180px" }}
        pt={{ canvas: { "aria-label": ariaLabel, role: "img" } }}
      />
      {/* Visually-hidden tabular equivalent for screen reader users (WCAG 1.1.1) */}
      <table className="sr-only">
        <caption>Recent assessment scores</caption>
        <thead>
          <tr>
            <th scope="col">Date</th>
            <th scope="col">Score</th>
          </tr>
        </thead>
        <tbody>
          {summaryRows.map(r => (
            <tr key={r.label}>
              <th scope="row">{r.label}</th>
              <td>{r.value}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

ScoreHistoryChart.displayName = "ScoreHistoryChart";

export { ScoreHistoryChart };
